import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ArrowRight, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function PortfolioDetail() {
  const { slug } = useParams();
  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["portfolio", slug], queryFn: async () => (await api.get(`/portfolio/${slug}`)).data, retry: false,
  });
  useSeo({ title: p?.title, description: p?.summary });

  if (isLoading) return <div className="bs-container py-24 text-center text-muted-foreground">Loading...</div>;
  if (isError || !p) return (
    <div className="bs-container py-24 text-center">
      <p className="text-muted-foreground">Project not found.</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/portfolio">Back to portfolio</Link></Button>
    </div>
  );

  return (
    <div className="bs-container py-16 lg:py-20">
      <Link to="/portfolio" className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1.5 h-4 w-4" /> Portfolio</Link>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{p.category}</Badge>
        {p.is_case_study && <Badge className="bg-accent/15 text-accent hover:bg-accent/15">Case study</Badge>}
      </div>
      <h1 className="mt-3 font-display text-4xl font-black sm:text-5xl">{p.title}</h1>
      <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{p.summary}</p>

      <img src={p.image} alt={p.title} className="mt-10 w-full rounded-3xl border border-border object-cover shadow-soft" />

      <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_0.5fr]">
        <div>
          <h2 className="font-display text-2xl font-bold">Overview</h2>
          <p className="mt-4 text-muted-foreground">{p.summary} Our team delivered this engagement end to end, from discovery and design through to secure engineering and launch.</p>
          {p.results?.length > 0 && (
            <>
              <h3 className="mt-8 font-display text-lg font-bold">Outcomes</h3>
              <div className="mt-4 space-y-3">
                {p.results.map((r) => <p key={r} className="flex items-start gap-3 text-sm"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" /> {r}</p>)}
              </div>
            </>
          )}
        </div>
        <aside className="h-fit rounded-2xl border border-border bg-card p-6">
          <dl className="space-y-4 text-sm">
            <div><dt className="text-muted-foreground">Client</dt><dd className="font-semibold">{p.client || "Confidential"}</dd></div>
            <div><dt className="text-muted-foreground">Category</dt><dd className="font-semibold">{p.category}</dd></div>
            <div><dt className="text-muted-foreground">Stack</dt><dd className="flex flex-wrap gap-1.5 pt-1">{(p.tags || []).map((t) => <Badge key={t} variant="outline">{t}</Badge>)}</dd></div>
          </dl>
          <Button asChild className="mt-6 w-full rounded-full"><Link to="/contact">Start a similar project <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </aside>
      </div>
    </div>
  );
}
